// src/api/index.js - GLOBAL TENANT INTERCEPTOR FOR ALL COMPONENTS
import axios from 'axios';
import { API_URL, attachCsrfToken } from './api1';

export { fetchCsrfToken, getCsrfToken } from './api1';

const API = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// ✅ AUTOMATIC TENANT HEADER FOR ALL API CALLS
API.interceptors.request.use(async (config) => {
  const tenantInfo = localStorage.getItem('tenantinfo');
  const userInfo = localStorage.getItem('userinfo');
  let tenantSubdomain = null;
  let userEmail = null;
  let userRole = null;
  
  // Extract tenant info
  if (tenantInfo) {
    try {
      const tenant = JSON.parse(tenantInfo);
      // Use tenant_id first, then fallback to other identifiers
      tenantSubdomain = tenant.tenant_id || tenant.id || tenant.tenant_name;
      // Ensure tenant_id is sent as number for proper resolution
      if (tenant.tenant_id || tenant.id) {
        tenantSubdomain = String(tenant.tenant_id || tenant.id);
      }
    } catch (e) {
      console.error('Failed to parse tenantinfo:', e);
    }
  }
  
  // Fallback to tenantsubdomain
  if (!tenantSubdomain) {
    tenantSubdomain = localStorage.getItem('tenantsubdomain');
  }
  
  // Extract user info
  if (userInfo) {
    try {
      const user = JSON.parse(userInfo);
      userEmail = user.email;
      userRole = user.role;
      console.log('👤 Extracted user info:', { email: userEmail, role: userRole });
    } catch (e) {
      console.error('❌ Failed to parse userinfo:', e, 'Raw data:', userInfo);
    }
  } else {
    console.log('⚠️ No userInfo found in localStorage');
  }
  
  // Set headers for API calls
  if (config.url?.includes('/api/')) {
    if (tenantSubdomain) {
      config.headers['X-Tenant-ID'] = tenantSubdomain;
    }
    if (userEmail) {
      config.headers['X-User-Email'] = userEmail;
    }
    if (userRole) {
      config.headers['X-User-Role'] = userRole;
    }
    console.log('🚀 API Request:', config.method?.toUpperCase(), config.url, 'Tenant:', tenantSubdomain, 'User:', userEmail, 'Role:', userRole);
  }

  // Attach a fresh single-use CSRF token to state-changing requests
  await attachCsrfToken(config);

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor to handle 403 errors gracefully
API.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 403 and the user is supposed to be logged in, redirect to login
    const isCsrfError = error.response?.data?.error === 'CSRF token validation failed';
    if (error.response?.status === 403 && !isCsrfError) {
      const userInfo = localStorage.getItem('userinfo');
      const tenantInfo = localStorage.getItem('tenantinfo');
      
      // Only redirect if user appears to be logged in but getting 403
      if (userInfo && tenantInfo) {
        console.warn('🚫 Got 403 error despite having user credentials, redirecting to login');
        localStorage.clear();
        window.location.href = '/';
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

export default API;
